<template>
  <div :class="{ 'phone-input--focused': focused, 'phone-input--disabled': disabled, 'has-error': error }">
    <ErrorMessage :error="error" />
    <input
      :id="id"
      ref="input"
      type="tel"
      :disabled="disabled"
      :name="name"
      :autocomplete="autocomplete"
      class="w-full bg-white border-0 outline-none rounded-r-md py-4"
      @focus="focused = true"
      @blur="focused = false"
      @keypress="onKeyPress"
      @input="change"
      @countrychange="change"
    />
    <div ref="container" class="absolute mt-1.5" />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue'
import intlTelInput from 'intl-tel-input'

import ErrorMessage from './ErrorMessage'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  label: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: undefined,
  },
  autocomplete: {
    type: String,
    default: undefined,
  },
  required: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: undefined,
  },
})

const emit = defineEmits(['update:modelValue', 'update:error'])

const { uid } = getCurrentInstance()
const id = computed(() => `phone-input-${uid}`)
const focused = ref()

const value = ref(props.modelValue)
watch(value, (val) => emit('update:modelValue', val))
watch(
  () => props.modelValue,
  (val) => (value.value = val),
)
function change() {
  value.value = iti && iti.isValidNumber() ? iti.getNumber() : null
  emit('update:error', undefined)
}

let iti
const input = ref()
const container = ref()

onMounted(() => {
  iti = intlTelInput(input.value, {
    customContainer: 'phone-input',
    dropdownContainer: container.value,
    separateDialCode: true,
    initialCountry: 'ru',
    preferredCountries: ['ru'],
    utilsScript: '/js/chunk-intl-utils.js',
  })
})
onUnmounted(() => {
  if (iti) {
    iti.destroy()
  }
})

function onKeyPress(e) {
  if (e.which < 48 || e.which > 57) {
    e.preventDefault()
  }
}
</script>

<style lang="postcss">
.phone-input input:-webkit-autofill,
.phone-input input:-webkit-autofill:hover,
.phone-input input:-webkit-autofill:focus {
  caret-color: white;
  -webkit-text-fill-color: white;
}
.phone-input--disabled .phone-input input:-webkit-autofill,
.phone-input--disabled .phone-input input:-webkit-autofill:hover,
.phone-input--disabled .phone-input input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 30px black inset !important;
}

.phone-input {
  @apply w-full inline-flex;
  @apply leading-8 text-base outline-none rounded-md;
  @apply bg-white border border-gray-500 text-black mb-4;
  @apply transition-all duration-300;
}
.phone-input input {
  @apply px-3 !important;
  box-shadow: none !important;
}
.has-error .phone-input {
  @apply border-red-600;
}
.iti__flag-container {
  @apply relative p-0;
}
.iti__selected-flag {
  @apply px-3;
  @apply leading-8 text-base outline-none rounded-l-md;
  @apply bg-gray-900 border-gray-500;
  @apply transition-all duration-300;
}
.phone-input--disabled .iti__selected-flag {
  @apply cursor-default ring-0 border-gray-500;
}

.iti__arrow {
  border-top-color: currentColor;
}
.iti__arrow--up {
  border-bottom-color: currentColor;
}

.iti--container {
  @apply top-0 left-0 p-0 !important;
  @apply overflow-hidden rounded-lg shadow z-50;
}
.iti__country-list {
  @apply relative max-h-60;
  @apply bg-gray-900 border border-gray-800;
}
@media (max-width: 500px) {
  .iti--container {
    width: 17rem;
  }
}
.iti__divider {
  border-bottom-color: #6b7280;
}
.iti__country.iti__highlight {
  @apply bg-gray-700;
}
</style>
